import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format cents to dollar string with proper formatting
 * @param cents - Amount in cents
 * @returns Formatted string like "$1,234.56"
 */
export function formatCents(cents: number): string {
  const dollars = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(dollars)
}

/**
 * Grand total for a quote in cents. Quote totalCents is tip-exclusive by
 * convention; the enforced (minimum) tip is added on top for the amount the
 * customer actually commits to.
 */
export function quoteGrandTotalCents(quote: {
  totalCents: bigint
  enforcedTipCents?: bigint
}): bigint {
  return quote.totalCents + (quote.enforcedTipCents ?? 0n)
}

/**
 * Format unit string from camelCase to space-separated format
 * Converts strings like "PerYear" to "Per Year", "PerEvent" to "Per Event", etc.
 * @param unit - Unit string in camelCase format
 * @returns Formatted string with spaces before capital letters
 */
export function formatUnit(unit: string): string {
  if (!unit) return unit

  // Add space before capital letters, but not before the first character
  return unit.replace(/([a-z])([A-Z])/g, '$1 $2')
}

const taxRateFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
})

/**
 * Format a tax rate fraction (e.g. 0.0825) as a percentage string with a
 * minimum of 2 and a maximum of 3 fraction digits, so a 3rd decimal only
 * surfaces when it's actually present.
 * @param fraction - Tax rate as a fraction (e.g., 0.0825 for 8.25%)
 * @returns Formatted percentage string without the "%" suffix (e.g., "8.25")
 */
export function formatTaxRateFraction(fraction: number): string {
  return taxRateFormatter.format(fraction * 100)
}
